import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setProps, ModalProps } from "../features/Slices/modalSlice";
import BulkBudgetEditor from "../components/BulkEditor/BulkBudgetEditor";
import BulkBudgetSubBudgetEditor from "../components/BulkEditor/BulkBudgetSubBudgetEditor";
import BulkBucketNodeEditor from "../components/BulkEditor/BulkBucketNodeEditor";
import BulkBudgetBucketNodeLinker from "../components/BulkEditor/BulkBudgetCategoryLinker";
import { setUseModalSpace } from "../features/Slices/modalSpaceSlice";
import { CurrentUser, FamilyBucketPluralLabel } from "../types";

interface WizardStep {
  key: string;
  label: string;
  kind: "budget" | "subBudget" | "bucketType" | "link";
  bucketTypeKey?: string;
}

const BulkEditWizard = (props: any) => {
  const modal: ModalProps = useSelector((state: any) => state.modal.value);
  const user: CurrentUser = useSelector((state: any) => state.userData.value);
  const dispatch = useDispatch();
  const budgetPluralLabel = FamilyBucketPluralLabel(user, "budget", "Budgets");
  const subBudgetPluralLabel = FamilyBucketPluralLabel(user, "subBudget", "SubBudgets");
  const bucketTypeSteps: WizardStep[] = (user.bucketDefinitions?.definitions ?? [])
    .filter(definition => definition.isEnabled)
    .filter(definition => definition.bucketTypeKey.toLowerCase() !== "budget")
    .filter(definition => definition.bucketTypeKey.toLowerCase() !== "subbudget")
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(definition => ({
      key: definition.bucketTypeKey,
      label: FamilyBucketPluralLabel(user, definition.bucketTypeKey, definition.pluralLabel || definition.bucketTypeKey),
      kind: "bucketType",
      bucketTypeKey: definition.bucketTypeKey,
    }));
  const stepItems: WizardStep[] = [
    { key: "budget", label: budgetPluralLabel, kind: "budget" },
    { key: "subBudget", label: subBudgetPluralLabel, kind: "subBudget" },
    ...bucketTypeSteps,
    { key: "link", label: `Link ${budgetPluralLabel}`, kind: "link" },
  ];
  const steps = stepItems.map(item => item.label);

  useEffect(() => {
    dispatch(setProps({ ...modal, IsOpen: false }));
    dispatch(setUseModalSpace(false));
    return () => { dispatch(setUseModalSpace(true)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const [step, setStep] = useState(0);
  const currentStep = stepItems[step];

  return (
    <>
      {currentStep?.kind === "budget" && (
        <BulkBudgetEditor step={step} onNext={handleNext} onBack={handleBack} steps={steps} />
      )}
      {currentStep?.kind === "subBudget" && (
        <BulkBudgetSubBudgetEditor step={step} onNext={handleNext} onBack={handleBack} steps={steps} />
      )}
      {currentStep?.kind === "bucketType" && currentStep.bucketTypeKey && (
        <BulkBucketNodeEditor step={step} onNext={handleNext} onBack={handleBack} steps={steps} bucketTypeKey={currentStep.bucketTypeKey} />
      )}
      {currentStep?.kind === "link" && (
        <BulkBudgetBucketNodeLinker step={step} onNext={handleNext} onBack={handleBack} steps={steps} />
      )}
    </>
  );

  function handleNext() {
    setStep((s) => Math.min(s + 1, steps.length - 1));
  }
  function handleBack() {
    setStep((s) => Math.max(s - 1, 0));
  }
};

export default BulkEditWizard;
